<template>
    <div
        :id="id"
        class="record"
        :class="{ 'active': isActive, 'skeleton': isSkeleton }"
        @click="onClick"
        @mouseenter="onMouseEnter"
        @mouseleave="onMouseLeave"
    >
        <div class="operation-name">
            <div
                class="operationName"
            >
                {{ isSkeleton ? '' : item.operationName }}
            </div>
            <div class="operationCategory">
                <span
                    class="operationCategoryStatusIndicator"
                    :class="item.fileClassification.toLowerCase()"
                />
                {{ item.fileClassificationDescription }}
            </div>
        </div>
        <div
            class="site-status"
        >
            <button
                v-if="showSiteStatus"
                class="siteStatusButton"
                :class="item.currentSiteStatus"
            >
                {{ item.currentSiteStatusDescription }}
            </button>
        </div>
        <div class="atsPbl">
            <span class="ats">
                ATS: <span
                    class="value"
                    :class="{ none: !ats }"
                >{{ ats || '(None)' }}</span>
            </span>
            <span class="ats">
                PBL: <span
                    class="value"
                    :class="{ none: !pbl }"
                >{{ pbl || '(None)' }}</span>
            </span>
        </div>
        <div class="docsCount">
            <span
                class="mdi mdi-text-box-multiple-outline"
            />
        </div>
        <div
            class="detail-btn"
            :class="{active:
                         item.currentSiteStatus,
                     notactive:
                         !item.currentSiteStatus}"
        >
            <span
                v-if="!isActive"
                class="mdi mdi-plus-circle-outline"
            />
            <span
                v-if="isActive"
                class="mdi mdi-close"
            />
        </div>
    </div>
    <div
        v-if="showReport"
        class="open-report"
    >
        <Details :site-details="item" />
    </div>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue';

import useApp from '@/composables';
import Details from '@/components/Details/Details.vue';
import ESARSite from '@/models/ESARSite';
import LegalLandDescription from '@/models/LegalLandDescription';

export default defineComponent({
    name: 'ListItem',
    components: { Details },
    props: {
        id: { type: String, required: true },
        item: {
            type: ESARSite,
            default: new ESARSite({
                id: '',
                operationName: '',
                documentsInFile: 0,
                coordinates: [],
                contaminants: [],
                LLD: [],
                riskManagementData: []
            })
        },
        isActive: {
            type: Boolean,
            default: false,
        },
        isSkeleton: {
            type: Boolean,
            default: false,
        },
        showSiteStatus: {
            type: Boolean,
            default: true,
        },
    },
    setup(props) {
        const { state, setOpenSite, setHoveredSite, removeHoveredSite } = useApp();

        const ats = computed(() => props.item.LLD.find((i:LegalLandDescription) => i.ATS)?.ATS);
        const pbl = computed(() => props.item.LLD.find((i:LegalLandDescription) => i.PBL)?.PBL);
        const showReport = computed(() => props.isActive && props.item && props.item.currentSiteStatus);

        const onClick = function (event: MouseEvent) {
            const clickedElement = (event.target as HTMLElement);
            if (clickedElement.className.includes('mdi-text-box-multiple-outline')) {
                window.open(props.item.documentsURL);
            } else if (props.item.id) {
                setOpenSite(props.item);
            }
        };

        const onMouseEnter = () => {
            if (state.hoveredSite && state.hoveredSite.id === props.item.id) {
                return;
            }
            setHoveredSite(props.item);
        };

        const onMouseLeave = () => {
            removeHoveredSite();
        };

        return {
            ats,
            pbl,
            showReport,
            onClick,
            onMouseEnter,
            onMouseLeave
        };
    }
});
</script>

<style lang="postcss" scoped>
 .record {
    @apply flex items-center py-2 px-2 border-b-2 border-gray-200 cursor-pointer;

    &:hover {
        @apply bg-blue-100 bg-opacity-50;

    }

    &.active {
        @apply bg-blue-100;
    }

    .operation-name,
    .site-status,
    .atsPbl,
    .docsCount,
    .detail-btn {
        @apply px-3 py-2;
    }

    .operation-name {
        width: 46%;

        .operationName {
            @apply block uppercase text-blue-400;
        }

        .operationCategory {
            @apply block text-sm;

            .operationCategoryStatusIndicator {
                @apply inline-block pr-2 rounded-full bg-black;
                width: 10px;
                height:10px;

                &.csu {
                    @apply bg-map-contaminated-site-upstream;
                }

                &.rec {
                    @apply bg-map-reclamation;
                }

                &.pst {
                    @apply bg-map-petroleum-storage-tank;
                }

                &.scd {
                    @apply bg-map-site-contamination-and-decomissioning;
                }
            }
        }
    }

    .site-status {
        width: 20%;

        .siteStatusButton {
            @apply border-2 rounded-full p-1 text-sm;
            @apply text-gray-500 border-gray-200;
            width: 150px;

            &.OPN, &.CER {
                @apply text-blue-400 bg-blue-400 bg-opacity-25 border-none;
            }
            &.PND, &.SUS {
                @apply text-red-600 bg-red-600 bg-opacity-25 border-none;
            }
        }
    }

    .atsPbl {
        @apply text-xs;
        width: 20%;

        .ats,
        .pbl {
            @apply block;
        }

        span.none {
            @apply text-gray-500;
        }
    }

    .docsCount {
        width: 7%;

        span {
            @apply block m-0 text-center;
        }

        span.mdi {
            font-size:25px;

            &.disabled {
                @apply text-grey-200;
            }
        }

        span.docsCountText {
            @apply text-xs;
            margin-top:-6px;
        }
    }

    .detail-btn {
        @apply text-4xl cursor-pointer;
        width: 7%;

        &.active span.mdi-plus-circle-outline{
            @apply text-blue-400;
        }

        &.notactive span.mdi-plus-circle-outline {
            @apply text-grey-200;
        }

        span.mdi-close {
            @apply text-3xl;
        }
    }

    &.skeleton {
        @apply animate-pulse;

        .operationName { @apply bg-gray-300 rounded h-6 mb-2 }
        .operationCategory {
            @apply bg-gray-300 rounded h-4;
            .operationCategoryStatusIndicator { @apply hidden; }
        }
        .site-status {
            @apply bg-gray-300 rounded h-8;
            .siteStatusButton { @apply hidden }
        }
        .atsPbl {
            .ats {
                 @apply text-gray-300 bg-gray-300 rounded h-4 mb-2;
                 span { @apply hidden }
             }
            .pbl {
                 @apply text-gray-300 bg-gray-300 rounded h-4;
                 span { @apply hidden }
             }
        }
    }
}

.open-report {
    @apply px-6 py-2 bg-blue-400 bg-opacity-4 border-b-2 border-gray-200;
}
</style>
